import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { HomeContentContainer } from '@/components'
import { Loader } from '@/components/common/Loader'
import { toast } from '@/components/common/Toast'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  BILL_LOCATION_LABELS,
  BILL_LOCATION_VALUES,
  type BillLocationValue,
} from '@/constants/bill-location'
import { useTranslations } from '@/hooks/useTranslations'
import { useProfileData } from '@/hooks/useProfile'
import {
  type CompanyProfile,
  type CompanyProfilesByLocation,
  useCompanyProfiles,
  useUpdateCompanyProfile,
} from '@/modules/company-profile/services/companyProfile'
import { canEditCompanyProfile } from '@/utils/sidebarNavVisibility'
import { AxiosError } from 'axios'

export const Route = createFileRoute('/(dashboard)/admin/company-profile')({
  component: CompanyProfilePage,
})

const emptyByLocation = (): CompanyProfilesByLocation =>
  Object.fromEntries(
    BILL_LOCATION_VALUES.map((loc) => [
      loc,
      { legal_name: '', address: '', phone: '' },
    ]),
  ) as CompanyProfilesByLocation

function CompanyProfilePage() {
  const t = useTranslations('admin.company_profile')
  const { checkPermission, isLoading: profileLoading } = useProfileData()
  const canEdit = canEditCompanyProfile(checkPermission)

  const { data, isLoading, isError } = useCompanyProfiles()
  const $update = useUpdateCompanyProfile()

  const [activeLocation, setActiveLocation] = useState<BillLocationValue>('chanthaburi')
  const [drafts, setDrafts] = useState<CompanyProfilesByLocation>(emptyByLocation)

  useEffect(() => {
    const rows = data?.data
    if (!rows) return
    setDrafts({
      chanthaburi: {
        legal_name: rows.chanthaburi?.legal_name ?? '',
        address: rows.chanthaburi?.address ?? '',
        phone: rows.chanthaburi?.phone ?? '',
      },
      chumphon: {
        legal_name: rows.chumphon?.legal_name ?? '',
        address: rows.chumphon?.address ?? '',
        phone: rows.chumphon?.phone ?? '',
      },
    })
  }, [data])

  const setDraftField = (
    location: BillLocationValue,
    field: keyof CompanyProfile,
    value: string,
  ) => {
    setDrafts((prev) => ({
      ...prev,
      [location]: { ...prev[location], [field]: value },
    }))
  }

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const d = drafts[activeLocation]
    try {
      await $update.mutateAsync({
        location: activeLocation,
        legal_name: d.legal_name.trim(),
        address: d.address.trim(),
        phone: d.phone.trim(),
      })
      toast.toastSuccess(t('toast_saved'))
    } catch (err) {
      const msg =
        AxiosError.isAxiosError(err) && err.response?.data
          ? (() => {
              const resp = err.response?.data as { message?: string | string[] }
              if (Array.isArray(resp?.message)) return resp.message.join(', ')
              if (typeof resp?.message === 'string') return resp.message
              return err.message
            })()
          : err instanceof Error
            ? err.message
            : t('toast_failed')
      toast.toastFailed(msg)
    }
  }

  if (profileLoading) {
    return <Loader />
  }

  if (!canEdit) {
    return (
      <HomeContentContainer title={t('title')}>
        <p className="text-muted-foreground">{t('no_access')}</p>
      </HomeContentContainer>
    )
  }

  if (isError) {
    return (
      <HomeContentContainer title={t('title')}>
        <p className="text-destructive">{t('load_error')}</p>
      </HomeContentContainer>
    )
  }

  return (
    <HomeContentContainer title={t('title')} contentClassName="max-w-full">
      {isLoading ? (
        <Loader />
      ) : (
        <Tabs
          value={activeLocation}
          onValueChange={(v) => setActiveLocation(v as BillLocationValue)}
          className="w-full"
        >
          <TabsList className="mb-4 grid w-full max-w-md grid-cols-2">
            {BILL_LOCATION_VALUES.map((loc) => (
              <TabsTrigger key={loc} value={loc} className="text-base">
                {BILL_LOCATION_LABELS[loc]}
              </TabsTrigger>
            ))}
          </TabsList>

          {BILL_LOCATION_VALUES.map((loc) => (
            <TabsContent key={loc} value={loc}>
              <form onSubmit={onSave} className="w-full">
                <Card className="w-full border-border shadow-sm">
                  <CardHeader className="space-y-1 pb-4">
                    <CardDescription className="text-base leading-relaxed">
                      {t('lead')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6 pt-0">
                    <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                      <div className="space-y-2 lg:col-span-2">
                        <Label htmlFor={`legal_name_${loc}`}>{t('field_legal_name')}</Label>
                        <Input
                          id={`legal_name_${loc}`}
                          className="min-h-11 w-full text-base"
                          value={drafts[loc].legal_name}
                          onChange={(e) => setDraftField(loc, 'legal_name', e.target.value)}
                          maxLength={500}
                          required
                        />
                      </div>
                      <div className="space-y-2 lg:col-span-2">
                        <Label htmlFor={`address_${loc}`}>{t('field_address')}</Label>
                        <Textarea
                          id={`address_${loc}`}
                          className="min-h-[140px] w-full resize-y text-base lg:min-h-[180px]"
                          value={drafts[loc].address}
                          onChange={(e) => setDraftField(loc, 'address', e.target.value)}
                          maxLength={2000}
                          required
                        />
                      </div>
                      <div className="space-y-2 lg:col-span-1 lg:max-w-md">
                        <Label htmlFor={`phone_${loc}`}>{t('field_phone')}</Label>
                        <Input
                          id={`phone_${loc}`}
                          className="min-h-11 w-full text-base"
                          value={drafts[loc].phone}
                          onChange={(e) => setDraftField(loc, 'phone', e.target.value)}
                          maxLength={80}
                          required
                        />
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="border-t bg-muted/30 pt-6">
                    <Button type="submit" className="min-h-11 min-w-[120px]" disabled={$update.isPending}>
                      {t('btn_save')}
                    </Button>
                  </CardFooter>
                </Card>
              </form>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </HomeContentContainer>
  )
}
